import { useEffect, useState } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import {
  Shield,
  ArrowRight,
  Server,
  Database,
  Network,
  Settings,
  Zap,
  Monitor,
  Cloud,
  Activity,
  Building,
  Heart,
  GraduationCap,
  ShoppingCart,
  Briefcase,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DataCenterPage = () => {
  useEffect(() => {
    document.title = 'Next-Generation Data Centers - Modernize Your Infrastructure | Atlas Defenders';

    // Force logo and text colors but keep background transparent initially
    const forceNavColors = () => {
      const header = document.querySelector('header');
      if (header) {
        // Force logo to be blue always
        const logoElements = header.querySelectorAll('.h-8.w-8, .text-xl.font-bold');
        logoElements.forEach(element => {
          element.classList.remove('text-white');
          element.classList.add('text-blue-600');
        });

        // Force all navigation links to be dark always
        const navLinks = header.querySelectorAll('nav a:not([class*="bg-gradient"]), nav button:not([class*="bg-gradient"])');
        navLinks.forEach(link => {
          link.classList.remove('text-white');
          link.classList.add('text-gray-900');
        });

        // Force background to be white always (User Request)
        header.classList.add('bg-white', 'shadow-sm');
        header.classList.remove('bg-transparent');
      }
    };

    // Apply colors immediately and keep them applied
    forceNavColors();

    // Keep colors applied even when header component tries to change them
    const interval = setInterval(forceNavColors, 200);

    // Cleanup function
    return () => {
      clearInterval(interval);
      // Reset colors when leaving the page
      const header = document.querySelector('header');
      if (header) {
        const logoElements = header.querySelectorAll('.h-8.w-8, .text-xl.font-bold');
        logoElements.forEach(element => {
          element.classList.remove('text-blue-600');
        });

        const navLinks = header.querySelectorAll('nav a, nav button');
        navLinks.forEach(link => {
          link.classList.remove('text-gray-900');
        });
      }
    };
  }, []);

  const [currentTechIndex, setCurrentTechIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [progress, setProgress] = useState(0);
  const [activeWhyChoose, setActiveWhyChoose] = useState(0);
  const [currentIndustryIndex, setCurrentIndustryIndex] = useState(0);

  // Interactive Tabs Data with detailed services
  const interactiveTabs = [
    {
      title: 'Physical Infrastructure',
      tagline: 'The Foundation',
      description: "Robust physical foundations featuring precision rack installation, structured cabling, UPS power systems, and advanced climate control for maximum uptime.",
      services: [
        {
          title: 'Power & UPS systems',
          features: [
            'Uninterruptible Power Supply (UPS) setup',
            'Power Distribution Units (PDU) management',
            'Backup generator integration',
            'Voltage regulation and surge protection'
          ],
          images: ['/Logos/Datacenter1.jpg']
        },
        {
          title: 'Rack installation & layout',
          features: [
            'Server rack assembly and mounting',
            'Optimal floor layout design',
            'Cable management solutions',
            'Seismic bracing and safety'
          ],
          images: ['/Logos/rack.jpg']
        },
        {
          title: 'Structured cabling & organization',
          features: [
            'Cat6/Cat6a/Fiber optic cabling',
            'Patch panel termination',
            'Cable labeling and color coding',
            'Vertical and functional cable management'
          ],
          images: ['/Logos/structured cabling.jpg']
        },
        {
          title: 'Cooling & airflow management',
          features: [
            'Hot/Cold aisle containment',
            'Precision air conditioning',
            'Environmental monitoring sensors',
            'Airflow optimization'
          ],
          images: ['/Logos/Datacenter2.jpg']
        },
        {
          title: 'Physical security',
          features: [
            'CCTV surveillance systems',
            'Biometric access control',
            'Intrusion detection systems',
            'Secure rack locking mechanisms'
          ],
          images: ['/Logos/monitoring_img.png']
        }
      ]
    },
    {
      title: 'Networking & Monitoring',
      tagline: 'Connectivity & Insight',
      description: "High-availability network architectures with comprehensive monitoring, ensuring seamless connectivity and real-time performance tracking.",
      services: [
        {
          title: 'Network design & architecture',
          features: [
            'High-availability network topology',
            'VLAN segmentation and routing',
            'Redundant core switching',
            'Scalable IP addressing schemes'
          ],
          images: ['/Logos/network_configuration.jpg']
        },
        {
          title: 'Wired & wireless infrastructure',
          features: [
            'Enterprise Wi-Fi 6/6E deployment',
            'Structured LAN cabling',
            'Switch and router configuration',
            'Wireless site surveys'
          ],
          images: ['/Logos/cabling.jpg']
        },
        {
          title: 'Routing, switching & load balancing',
          features: [
            'Dynamic routing protocols (OSPF, BGP)',
            'Layer 2/3 switching optimization',
            'Application load balancing',
            'Traffic shaping and QoS'
          ],
          images: ['/Logos/Datacenter1.jpg']
        },
        {
          title: 'Firewalls & network security',
          features: [
            'Next-Gen Firewall (NGFW) setup',
            'Intrusion Prevention Systems (IPS)',
            'VPN (Site-to-Site, Remote Access)',
            'Network Access Control (NAC)'
          ],
          images: ['/Logos/Datacenter2.jpg']
        },
        {
          title: 'Infrastructure & network monitoring',
          features: [
            '24/7 Real-time performance tracking',
            'Bandwidth usage analysis',
            'Device health status alerting',
            'Log management and reporting'
          ],
          images: ['/Logos/monitoring_img.png']
        }
      ]
    },
    {
      title: 'Compute & Virtualization',
      tagline: 'Processing Power',
      description: "Scalable compute platforms and virtualization technologies designed to resource-optimize your workloads and ensure high availability.",
      services: [
        {
          title: 'Server racking & configuration',
          features: [
            'Hardware assembly and mounting',
            'BIOS/Firmware updates',
            'RAID configuration',
            'Remote management setup (iDRAC/iLO)'
          ],
          images: ['/Logos/server cabling.jpg']
        },
        {
          title: 'Compute platforms',
          features: [
            'High-performance computing clusters',
            'Blade server systems',
            'Hyperconverged infrastructure (HCI)',
            'GPU acceleration nodes'
          ],
          images: ['/Logos/Datacenter1.jpg']
        },
        {
          title: 'Virtualization & private cloud',
          features: [
            'VMware vSphere / Hyper-V deployment',
            'Virtual Machine resource management',
            'Private cloud orchestration',
            'Containerization (Docker/Kubernetes)'
          ],
          images: ['/Logos/Datacenter2.jpg']
        },
        {
          title: 'High availability & optimization',
          features: [
            'Failover clustering',
            'Load balancing across nodes',
            'Resource pooling and DRS',
            'Performance tuning and analysis'
          ],
          images: ['/Logos/network_configuration.jpg']
        }
      ]
    },
    {
      title: 'Systems & Core Services',
      tagline: 'Operational Backbone',
      description: "Essential core services including Windows and Linux server administration, identity management, and secure directory services.",
      services: [
        {
          title: 'Windows Server services',
          features: [
            'Active Directory (AD) & Group Policy',
            'DNS & DHCP configuration',
            'Distributed File System (DFS)',
            'SCCM & WDS deployment'
          ],
          images: ['/Logos/Datacenter1.jpg']
        },
        {
          title: 'Linux server deployment',
          features: [
            'Red Hat / Ubuntu / CentOS hardening',
            'SSH security and key management',
            'Package management and automation',
            'Kernel tuning and optimization'
          ],
          images: ['/Logos/Datacenter2.jpg']
        },
        {
          title: 'File & identity services',
          features: [
            'Centralized identity management (IAM)',
            'Secure file sharing solutions',
            'LDAP / Radius integration',
            'Access control lists (ACLs)'
          ],
          images: ['/Logos/network_configuration.jpg']
        }
      ]
    },
    {
      title: 'Cloud Platforms',
      tagline: 'Scalable Horizons',
      description: "Flexible private, hybrid, and multi-cloud solutions tailored to your business needs, ensuring secure connectivity and seamless migration.",
      services: [
        {
          title: 'Private, hybrid & multi-cloud',
          features: [
            'Custom private cloud architecture',
            'Hybrid cloud integration (Azure/AWS)',
            'Multi-cloud management strategies',
            'Cloud resource orchestration'
          ],
          images: ['https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop']
        },
        {
          title: 'Secure cloud connectivity',
          features: [
            'Direct Connect / ExpressRoute setup',
            'Secure SD-WAN integration',
            'Encrypted data transit',
            'Cloud firewall configuration'
          ],
          images: ['https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=800&h=600&fit=crop']
        },
        {
          title: 'Workload migration',
          features: [
            'Physical to Virtual (P2V) migration',
            'Cloud lift-and-shift strategies',
            'Database migration services',
            'Minimal downtime cutover planning'
          ],
          images: ['https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop']
        }
      ]
    },
    {
      title: 'Cybersecurity',
      tagline: 'Active Defense',
      description: "Advanced defense mechanisms including network security, endpoint protection, and continuous threat detection to safeguard your digital assets.",
      services: [
        {
          title: 'Network & endpoint security',
          features: [
            'Endpoint protection platforms (EPP)',
            'Network segmentation',
            'Device encryption and control',
            'Zero Trust architecture'
          ],
          images: ['https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop']
        },
        {
          title: 'SIEM, EDR, XDR & SOAR',
          features: [
            'Security Information & Event Mgmt',
            'Endpoint Detection & Response',
            'Extended Detection & Response',
            'Security Orchestration & Response'
          ],
          images: ['https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop']
        },
        {
          title: 'Threat detection & response',
          features: [
            'Continuous threat hunting',
            'Automated incident response',
            'Vulnerability assessments',
            'Phishing simulation and training'
          ],
          images: ['https://images.unsplash.com/photo-1504384308090-c54be385363d?w=800&h=600&fit=crop']
        }
      ]
    }
  ];

  // Reset progress when tab changes manually
  const handleTabClick = (index: number) => {
    setActiveTab(index);
  };

  // What We Deliver Services
  const deliverables = [
    { title: 'Virtualization & Hyperconverged Infrastructure', subtitle: 'VMware, Nutanix, Proxmox', icon: Server },
    { title: 'Windows & Linux Server Administration', subtitle: 'Enterprise server management', icon: Monitor },
    { title: 'Cloud-Ready & Hybrid Architecture', subtitle: 'Seamless cloud integration', icon: Cloud },
    { title: 'Network Design & Firewall Configuration', subtitle: 'Secure network infrastructure', icon: Network },
    { title: 'Rack, Cabling & Power Planning', subtitle: 'Physical infrastructure design', icon: Zap },
    { title: 'Infrastructure Monitoring & Automation', subtitle: 'Proactive system management', icon: Activity },
    { title: 'Backup & Disaster Recovery Solutions', subtitle: 'Business continuity planning', icon: Database },
    { title: 'Zero Trust Security & Access Control', subtitle: 'Advanced security framework', icon: Shield }
  ];

  // Technology Stack
  const techStacks = [
    {
      category: 'Virtualization',
      technologies: ['VMware', 'Nutanix', 'Hyper-V', 'Proxmox']
    },
    {
      category: 'Networking',
      technologies: ['Cisco', 'Juniper', 'Fortinet', 'Palo Alto']
    },
    {
      category: 'Automation',
      technologies: ['Ansible', 'Terraform', 'Zabbix', 'PRTG']
    },
    {
      category: 'Operating Systems',
      technologies: ['Windows Server', 'Red Hat', 'Ubuntu', 'Debian']
    },
    {
      category: 'Cloud Platforms',
      technologies: ['AWS', 'Azure', 'GCP']
    },
    {
      category: 'Network Hardware',
      technologies: ['Ubiquiti', 'MikroTik', 'Aruba', 'Meraki']
    }
  ];

  // Certifications
  const certifications = [
    'VCP-DCV (VMware Certified Professional – Data Center Virtualization)',
    'NCP-MCI (Nutanix Certified Professional – Multicloud Infrastructure)',
    'CCNA, JNCIA, Fortinet NSE 4–8, PCNSE',
    'RHCSA, LPIC, AZ-800/801, AWS Certified Architect',
    'GCP Associate / Pro Architect, ITIL, ISO 27001 / 22301'
  ];

  // Next-Gen Services
  const nextGenServices = [
    {
      title: 'Virtualization & Compute Infrastructure',
      features: [
        'Server provisioning and clustering (vSphere, AHV, KVM)',
        'Centralized compute resource pools',
        'High availability and fault tolerance'
      ],
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop'
    },
    {
      title: 'Network Architecture & Security',
      features: [
        'LAN/WAN design with segmentation',
        'Firewall and ACL configuration (FortiGate, Palo Alto, pfSense)',
        'VPN setup and zero-trust network models'
      ],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'
    },
    {
      title: 'Monitoring & Automation',
      features: [
        'Real-time network monitoring (Zabbix, PRTG)',
        'Infrastructure-as-Code (Ansible, Terraform)',
        'Log collection and alerting systems (Syslog, Graylog)'
      ],
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop'
    },
    {
      title: 'Cloud & Hybrid Deployments',
      features: [
        'Integration with AWS, Azure, GCP',
        'Hybrid solutions (e.g., Azure Stack, vCloud Director)',
        'Identity Federation & SSO (AD, ADFS, Keycloak)'
      ],
      image: 'https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=600&h=400&fit=crop'
    },
    {
      title: 'Rack Deployment & Cabling',
      features: [
        'Physical setup, labeling, and patch panel design',
        'Power planning and cooling (PDU, UPS)',
        'Wireless deployment (Access Points, Controllers)'
      ],
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop'
    }
  ];

  // Why Choose Atlas Defenders - Interactive Content
  const whyChooseUsData = [
    {
      id: 0,
      title: 'Expert Engineering',
      subtitle: 'Certified engineers with multi-vendor expertise',
      description: 'Our team of certified professionals brings decades of combined experience across all major infrastructure platforms and technologies.',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop',
      features: [
        'Industry-certified professionals with extensive hands-on experience',
        'Multi-vendor expertise across leading technology platforms',
        'Continuous training and certification maintenance programs',
        'Deep technical knowledge in emerging technologies'
      ]
    },
    {
      id: 1,
      title: 'Hybrid Expertise',
      subtitle: 'Experience in private, public, and hybrid environments',
      description: 'Seamless integration across on-premises, cloud, and hybrid infrastructures with proven methodologies and best practices.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop',
      features: [
        'Proven track record in complex hybrid deployments',
        'Cloud-native and traditional infrastructure expertise',
        'Seamless migration and integration strategies',
        'Cost optimization across multi-cloud environments'
      ]
    },
    {
      id: 2,
      title: 'Tailored Solutions',
      subtitle: 'Scalable designs tailored to your industry',
      description: 'Custom-engineered solutions that align with your specific business requirements, compliance needs, and growth objectives.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop',
      features: [
        'Industry-specific compliance and regulatory expertise',
        'Scalable architectures that grow with your business',
        'Custom integration with existing systems and workflows',
        'Performance optimization for your specific use cases'
      ]
    },
    {
      id: 3,
      title: 'Security First',
      subtitle: 'Secure-by-default implementations',
      description: 'Comprehensive security frameworks integrated from the ground up, ensuring your infrastructure is protected against evolving threats.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop',
      features: [
        'Zero-trust security architecture implementation',
        'Advanced threat detection and response systems',
        'Compliance with industry security standards',
        'Regular security assessments and vulnerability management'
      ]
    },
    {
      id: 4,
      title: 'Complete Documentation',
      subtitle: 'Post-deployment documentation and training',
      description: 'Comprehensive documentation and knowledge transfer ensuring your team can effectively manage and maintain your infrastructure.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
      features: [
        'Detailed technical documentation and runbooks',
        'Comprehensive training programs for your technical staff',
        'Best practices guides and troubleshooting procedures',
        'Ongoing knowledge transfer and skill development'
      ]
    },
    {
      id: 5,
      title: 'Ongoing Support',
      subtitle: 'Comprehensive support and maintenance',
      description: 'Proactive monitoring, maintenance, and support services ensuring optimal performance and minimal downtime for your critical systems.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop',
      features: [
        '24/7 monitoring and proactive issue resolution',
        'Predictive maintenance and performance optimization',
        'Rapid response times for critical incidents',
        'Regular health checks and system updates'
      ]
    }
  ];

  // Industries We Serve - Complete Data with Relevant Images
  const industriesData = [
    {
      name: 'Finance & Banking',
      icon: Building,
      description: 'Comprehensive data center solutions for financial institutions with SOX compliance, real-time transaction processing, fraud detection systems, and regulatory reporting infrastructure. Our secure environments ensure 99.99% uptime for critical banking operations.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=800&fit=crop', // Financial data/charts
      badges: ['SOX Compliance', 'PCI DSS Level 1'],
      gradient: 'from-blue-500/40 via-indigo-600/30 to-purple-700/40'
    },
    {
      name: 'Healthcare',
      icon: Heart,
      description: 'HIPAA-compliant data centers designed for hospitals, clinics, and healthcare providers. Secure patient data storage, medical imaging systems, electronic health records (EHR), and telemedicine infrastructure with end-to-end encryption.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=800&fit=crop', // Medical equipment/hospital
      badges: ['HIPAA Compliant', 'HITECH Act'],
      gradient: 'from-emerald-500/40 via-teal-600/30 to-cyan-700/40'
    },
    {
      name: 'E-Commerce & Cloud Providers',
      icon: ShoppingCart,
      description: 'Scalable cloud infrastructure for e-commerce platforms, payment processing systems, inventory management, and customer data protection. High-performance computing resources with auto-scaling capabilities for peak traffic periods.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=800&fit=crop', // E-commerce/online shopping
      badges: ['PCI DSS', 'ISO 27001'],
      gradient: 'from-orange-500/40 via-red-600/30 to-pink-700/40'
    },
    {
      name: 'Government & Public Sector',
      icon: Shield,
      description: 'FedRAMP-authorized data centers for federal, state, and local government agencies. FISMA-compliant infrastructure supporting classified data processing, citizen services, emergency response systems, and inter-agency communications.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&h=800&fit=crop', // Government building/security
      badges: ['FedRAMP Authorized', 'FISMA Compliant'],
      gradient: 'from-slate-600/40 via-gray-700/30 to-zinc-800/40'
    },
    {
      name: 'Critical Infrastructure',
      icon: Zap,
      description: 'Hardened data centers for power grids, water treatment facilities, transportation systems, and telecommunications networks. NERC CIP compliance, industrial control systems (ICS/SCADA), and 24/7 monitoring for national security.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=800&fit=crop', // Power plant/industrial infrastructure
      badges: ['NERC CIP', 'ICS Security'],
      gradient: 'from-yellow-500/40 via-orange-600/30 to-red-700/40'
    },
    {
      name: 'Education & Research',
      icon: GraduationCap,
      description: 'Academic data centers supporting universities, research institutions, and K-12 schools. FERPA-compliant student information systems, research computing clusters, distance learning platforms, and collaborative research networks.',
      image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&h=800&fit=crop', // University/education
      badges: ['FERPA Compliant', 'Research Grade'],
      gradient: 'from-green-500/40 via-emerald-600/30 to-teal-700/40'
    },
    {
      name: 'Professional Services',
      icon: Briefcase,
      description: 'Secure data centers for law firms, consulting companies, accounting firms, and professional service providers. Client confidentiality protection, document management systems, secure communications, and compliance reporting.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop', // Professional office/business
      badges: ['Attorney-Client Privilege', 'Professional Standards'],
      gradient: 'from-purple-500/40 via-indigo-600/30 to-blue-700/40'
    },
    {
      name: 'Manufacturing & Industrial',
      icon: Settings,
      description: 'Industrial-grade data centers for manufacturing operations, supply chain management, IoT sensor networks, predictive maintenance systems, and quality control processes. Integration with ERP, MES, and automation systems.',
      image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=600&h=800&fit=crop', // Manufacturing/industrial
      badges: ['Industry 4.0', 'IoT Security'],
      gradient: 'from-gray-500/40 via-slate-600/30 to-zinc-700/40'
    }
  ];

  // Auto-advance carousel with pause on hover/interaction
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  useEffect(() => {
    if (isCarouselPaused) return; // Don't auto-advance when paused

    const interval = setInterval(() => {
      setCurrentIndustryIndex((prev) => (prev + 1) % industriesData.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [industriesData.length, isCarouselPaused]);

  // Navigation functions
  const nextIndustry = () => {
    setCurrentIndustryIndex((prev) => (prev + 1) % industriesData.length);
  };

  const prevIndustry = () => {
    setCurrentIndustryIndex((prev) => (prev - 1 + industriesData.length) % industriesData.length);
  };

  const goToIndustry = (index: number) => {
    setCurrentIndustryIndex(index);
  };

  return (
    <>
      {/* 1. HERO SECTION - Netgear Exact Match (Angular/Geometric) WITH CAROUSEL */}
      <section className="relative min-h-[700px] lg:min-h-[850px] bg-white overflow-hidden">
        {/* Slides Data */}
        {(() => {
          const slides = [
            {
              id: 1,
              image: "/images/datacenter1.png",
              title1: "Network Solutions",
              title2: "for Your Business",
              desc: "Atlas Defenders Enterprise combines performance, simplicity, and reliability — enabling seamless deployment, remote management, and integration across enterprise, broadcast, and AV markets."
            },
            {
              id: 2,
              image: "/images/Server administration 4.png",
              title1: "Advanced Server",
              title2: "Administration",
              desc: "Proactive server management and optimization to ensure maximum uptime, security, and performance for your critical business applications."
            },
            {
              id: 3,
              image: "/images/Server administration 6.png",
              title1: "Scalable Cloud",
              title2: "Infrastructure",
              desc: "Future-proof your IT landscape with scalable, secure, and high-performance cloud infrastructure solutions tailored to your enterprise needs."
            }
          ];

          const [currentSlide, setCurrentSlide] = useState(0);

          useEffect(() => {
            const timer = setInterval(() => {
              setCurrentSlide((prev) => (prev + 1) % slides.length);
            }, 6000); // 6 seconds per slide
            return () => clearInterval(timer);
          }, []);

          return (
            <>
              <div className="container mx-auto px-8 lg:px-16 relative z-10 h-full flex items-center min-h-[550px] lg:min-h-[650px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">

                  {/* Left Content - CAROUSEL */}
                  <div className="max-w-xl mx-auto translate-y-24 -translate-x-28 py-12 lg:py-0 z-20 min-h-[400px] flex flex-col justify-center">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentSlide}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="flex items-center space-x-3 mb-8">
                          <img src="/Logos/atlasdefenders_logo_blue_only.svg" alt="" className="h-10 w-auto" />
                          <div className="text-slate-800 font-bold text-sm tracking-wider uppercase">ENTERPRISE</div>
                        </div>

                        <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                          <span className="text-blue-600">{slides[currentSlide].title1}</span><br />
                          <span className="text-slate-900">{slides[currentSlide].title2}</span>
                        </h1>

                        <p className="text-base text-slate-700 leading-relaxed max-w-md">
                          {slides[currentSlide].desc}
                        </p>
                      </motion.div>
                    </AnimatePresence>

                    {/* Carousel Indicators */}
                    <div className="flex space-x-3 mt-8">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-blue-600' : 'w-2 bg-slate-300'}`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>

                </div>
              </div>

              {/* Right Angular Geometric Shapes & Images - CAROUSEL */}
              <div className="absolute top-0 right-0 bottom-0 w-[55%] z-10"
                style={{ clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0% 100%)' }}>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={slides[currentSlide].image}
                      alt="Infrastructure"
                      className="w-full h-full object-cover object-[30%_center]"
                      onError={(e) => {
                        // Fallback logic if needed
                        if (slides[currentSlide].image.includes('Server')) {
                          e.currentTarget.src = "/Logos/Datacenter1.jpg";
                        }
                      }}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Blue Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-l from-blue-900/40 via-blue-800/20 to-transparent mix-blend-multiply pointer-events-none"></div>
              </div>
            </>
          );
        })()}
      </section>



      {/* 2.5. WHAT WE DELIVER - Dell Showcase Style (Center Mode) */}
      <section className="relative min-h-screen overflow-hidden flex flex-col justify-center py-20">

        {/* Modern Blue Background & Advanced Lighting Effects */}
        <div className="absolute inset-0 bg-[#020617] z-0">
          {/* Main Gradient Field */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/40 via-slate-950 to-black"></div>

          {/* Top Center Blue Glow - "Spotlight" effect */}
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1000px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] mix-blend-screen pointer-events-none"></div>

          {/* Bottom Right Subtle Indigo Glow */}
          <div className="absolute bottom-[-10%] right-[-10%] w-[800px] h-[800px] bg-indigo-900/20 rounded-full blur-[100px] mix-blend-screen pointer-events-none"></div>

          {/* Bottom Left Cyan Glow */}
          <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-cyan-900/10 rounded-full blur-[100px] mix-blend-screen pointer-events-none"></div>
        </div>

        {/* Section Header */}
        <div className="container mx-auto px-6 mb-12 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-left"
          >
            <h3 className="text-gray-400 font-medium text-sm lg:text-base uppercase tracking-widest mb-2">
              ATLAS DEFENDERS CORE SERVICES
            </h3>
            <div className="h-14 overflow-hidden relative">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="text-white text-3xl lg:text-5xl font-light absolute top-0 left-0"
                >
                  {interactiveTabs[activeTab].title}
                </motion.h2>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* Carousel Logic */}
        <div className="relative w-full h-[60vh] lg:h-[70vh] flex items-center justify-center">

          <div className="relative w-full max-w-[1400px] h-full mx-auto perspective-1000">
            {interactiveTabs.map((tab, index) => {
              // Calculate position relative to active tab
              let position = 0; // 0 = active, -1 = prev, 1 = next, etc.
              const len = interactiveTabs.length;

              // Logic to determine shortest path wrap-around distance
              if (index === activeTab) {
                position = 0;
              } else if (index === (activeTab - 1 + len) % len) {
                position = -1;
              } else if (index === (activeTab + 1) % len) {
                position = 1;
              } else {
                position = 2; // Hidden/Far
              }

              // Determine z-index and opacity based on position
              const isActive = position === 0;
              const isPrev = position === -1;
              const isNext = position === 1;

              // Framer Motion variants for positions
              const variants = {
                active: {
                  x: "0%",
                  scale: 1,
                  opacity: 1,
                  zIndex: 30,
                  filter: "brightness(1) blur(0px)"
                },
                prev: {
                  x: "-75%",
                  scale: 0.85,
                  opacity: 0.4,
                  zIndex: 10,
                  filter: "brightness(0.5) blur(1px)"
                },
                next: {
                  x: "75%",
                  scale: 0.85,
                  opacity: 0.4,
                  zIndex: 10,
                  filter: "brightness(0.5) blur(1px)"
                },
                hidden: {
                  x: position === 2 ? "150%" : "-150%",
                  scale: 0.5,
                  opacity: 0,
                  zIndex: 0
                }
              };

              const currentVariant = isActive ? "active" : isPrev ? "prev" : isNext ? "next" : "hidden";

              return (
                <motion.div
                  key={index}
                  initial={false}
                  animate={currentVariant}
                  variants={variants}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center pointer-events-none"
                  style={{
                    // Ensure clicks pass through to arrows, but catch clicks on content if needed
                    pointerEvents: isActive ? 'auto' : 'none'
                  }}
                >
                  {/* IMAGE CONTAINER */}
                  <div className="relative w-[70%] lg:w-[60%] h-full shadow-2xl flex items-center justify-center">
                    <div className="w-full h-full relative overflow-hidden bg-gray-900">
                      <img
                        src={tab.services[0]?.images ? tab.services[0].images[0] : '/Logos/Datacenter1.jpg'}
                        alt={tab.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Subtle Gradient Overlay on Image */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-50"></div>
                    </div>

                    {/* Floating Content Card - Only visible on Active Slide */}
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 w-[400px] bg-white p-8 shadow-2xl z-40 border-l-4 border-blue-600"
                      >
                        <div className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">
                          Support And Services
                        </div>
                        <h3 className="text-3xl font-light text-gray-900 mb-4 leading-tight">
                          {tab.title}
                        </h3>
                        <p className="text-gray-600 mb-6 text-sm leading-relaxed line-clamp-4">
                          {tab.description}
                        </p>

                        <a href={`#service-category-${index}`} className="inline-flex items-center text-blue-600 font-semibold hover:gap-2 transition-all group-hover/link text-sm">
                          Learn More <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/link:translate-x-1" />
                        </a>
                      </motion.div>
                    )}

                    {/* Mobile Content Card (Bottom) */}
                    {isActive && (
                      <div className="lg:hidden absolute bottom-0 left-0 right-0 bg-white p-6 shadow-xl z-40">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{tab.title}</h3>
                        <a href={`#service-category-${index}`} className="flex items-center text-blue-600 text-sm font-semibold">
                          Learn More <ArrowRight className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    )}

                  </div>
                </motion.div>
              );
            })}

            {/* Navigation Controls */}
            <div className="absolute top-1/2 left-4 lg:left-8 -translate-y-1/2 z-50">
              <button
                onClick={() => handleTabClick((activeTab - 1 + interactiveTabs.length) % interactiveTabs.length)}
                className="bg-white/10 hover:bg-white text-white hover:text-black p-3 lg:p-4 rounded-none backdrop-blur-md transition-all duration-300"
              >
                <ChevronLeft className="w-6 h-6 lg:w-8 lg:h-8" />
              </button>
            </div>

            <div className="absolute top-1/2 right-4 lg:right-8 -translate-y-1/2 z-50">
              <button
                onClick={() => handleTabClick((activeTab + 1) % interactiveTabs.length)}
                className="bg-white/10 hover:bg-white text-white hover:text-black p-3 lg:p-4 rounded-none backdrop-blur-md transition-all duration-300"
              >
                <ChevronRight className="w-6 h-6 lg:w-8 lg:h-8" />
              </button>
            </div>

          </div>
        </div>

        {/* Pagination Dots */}
        <div className="container mx-auto px-6 mt-12 flex justify-center space-x-3">
          {interactiveTabs.map((_, index) => (
            <button
              key={index}
              onClick={() => handleTabClick(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${activeTab === index ? 'bg-blue-500 w-6' : 'bg-gray-700 hover:bg-gray-500'}`}
            />
          ))}
        </div>

      </section>





      {/* 2.6. OUR TECHNOLOGY STACK - Zabbix Style with Hexagonal Image */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-6 relative z-10 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Technology <span className="text-blue-600">Stack</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We leverage best-in-class technologies to build resilient, high-performance infrastructure.
            </p>
          </motion.div>
        </div>

        {/* Full Width Image Container */}
        <div className="w-full relative">


          <img
            src="/images/datacenter_engineer_solution.png"
            alt="Our Technology Stack"
            className="w-full h-auto object-cover min-h-[400px]"
          />

          {/* Optional: Bottom fade if needed for the next section matching */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-transparent to-transparent z-10"></div>
        </div>
      </section >

      {/* 6. WHY CHOOSE ATLAS DEFENDERS */}
      < section className="py-24 bg-gray-50" >
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">
                Why Choose
              </h2>
              <h2 className="text-4xl font-bold mb-6">
                Atlas <span className="text-blue-600">Defenders?</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven expertise and commitment to delivering exceptional infrastructure solutions.
            </p>
          </div>

          {/* Interactive Services Layout - Exact Style from Reference */}
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-8 items-stretch">
              {/* Left Sidebar Menu */}
              <div className="lg:col-span-2 space-y-3 flex flex-col">
                {whyChooseUsData.map((item, index) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    onClick={() => setActiveWhyChoose(item.id)}
                    className={`relative w-full text-left p-5 rounded-xl shadow-lg transition-all duration-500 group overflow-hidden transform hover:scale-105 hover:-translate-y-1 ${activeWhyChoose === item.id
                      ? 'bg-gradient-to-b from-blue-900 via-blue-950 to-slate-900 shadow-2xl shadow-blue-500/25'
                      : 'bg-white hover:bg-gradient-to-b hover:from-blue-900 hover:via-blue-950 hover:to-slate-900 hover:shadow-2xl hover:shadow-blue-500/25'
                      }`}
                  >
                    {/* Background shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

                    {/* Content */}
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex-1">
                        <span className={`text-lg font-bold transition-all duration-300 ${activeWhyChoose === item.id
                          ? 'text-white'
                          : 'text-gray-900 group-hover:text-white'
                          }`}>
                          {item.title}
                        </span>
                        <div className={`text-sm mt-1 transition-all duration-300 ${activeWhyChoose === item.id
                          ? 'text-blue-100'
                          : 'text-gray-500 group-hover:text-blue-100'
                          }`}>
                          {item.subtitle}
                        </div>
                      </div>

                      <div className={`ml-4 p-2 rounded-full transition-all duration-300 ${activeWhyChoose === item.id
                        ? 'bg-white/20'
                        : 'bg-gray-100 group-hover:bg-white/20'
                        }`}>
                        <ChevronRight className={`w-5 h-5 transition-all duration-300 ${activeWhyChoose === item.id
                          ? 'text-white'
                          : 'text-gray-600 group-hover:text-white'
                          }`} />
                      </div>
                    </div>

                    {/* Active indicator */}
                    {activeWhyChoose === item.id && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.3 }}
                        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-400"
                      />
                    )}

                    {/* Hover glow effect */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                  </motion.button>
                ))}
              </div>

              {/* Right Content Area - Dynamic */}
              <div className="lg:col-span-3 flex">
                <motion.div
                  key={activeWhyChoose}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="relative bg-gradient-to-b from-blue-900 via-blue-950 to-slate-900 rounded-2xl overflow-hidden shadow-2xl w-full flex flex-col justify-between"
                  style={{ minHeight: 'calc(6 * 5rem + 5 * 0.75rem)' }} // 6 items * height + 5 gaps
                >


                  {/* Content */}
                  <div className="relative z-10 p-8 flex flex-col justify-between h-full">
                    {/* Top Section */}
                    <div className="space-y-6">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="space-y-3"
                      >
                        <h3 className="text-4xl font-bold text-white leading-tight">
                          {whyChooseUsData[activeWhyChoose].title}
                        </h3>
                        <p className="text-cyan-300 text-base font-semibold">
                          {whyChooseUsData[activeWhyChoose].subtitle}
                        </p>
                      </motion.div>

                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="text-blue-100 text-lg leading-relaxed"
                      >
                        {whyChooseUsData[activeWhyChoose].description}
                      </motion.p>
                    </div>

                    {/* Features List */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                      className="space-y-4"
                    >
                      <h4 className="text-xl font-semibold text-white mb-4">Key Benefits:</h4>
                      <ul className="space-y-4">
                        {whyChooseUsData[activeWhyChoose].features.map((feature, index) => (
                          <motion.li
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: 0.6 + (index * 0.1) }}
                            className="flex items-start text-gray-200"
                          >
                            <div className="w-3 h-3 bg-cyan-400 rounded-full mt-1.5 mr-4 flex-shrink-0"></div>
                            <span className="leading-relaxed text-base">{feature}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>

                    {/* Bottom CTA Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 }}
                      className="pt-8 border-t border-white/20 flex justify-center"
                    >
                      <Link
                        to="/contact"
                        className="group relative inline-flex items-center justify-center bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-500 shadow-2xl hover:shadow-white/25 hover:scale-105 hover:-translate-y-1 overflow-hidden"
                      >
                        {/* Animated background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-50 via-white to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

                        {/* Button content */}
                        <div className="relative z-10 flex items-center space-x-3">
                          <span className="transition-all duration-300 group-hover:text-blue-600">
                            Get Started Today
                          </span>
                          <ArrowRight className="w-5 h-5 transition-all duration-300 group-hover:translate-x-1 group-hover:text-blue-600" />
                        </div>

                        {/* Glow effect */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-white to-blue-400 rounded-xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                      </Link>
                    </motion.div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-20 h-20 opacity-10">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="2" fill="none" className="text-blue-400" />
                      <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="1" fill="none" className="text-blue-400" />
                    </svg>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* 7. INDUSTRIES WE SERVE - Professional Carousel Design */}
      < section className="py-24 bg-gradient-to-br from-white to-gray-50" >
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-5xl font-bold text-gray-900 mb-4">
                  Industries We
                </h2>
                <h2 className="text-5xl font-bold text-blue-600 mb-6">
                  Serve
                </h2>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed max-w-lg">
                Atlas Defenders provides specialized data center solutions across critical industries, ensuring
                compliance and protection tailored to each sector's unique requirements.
              </p>

              <Link
                to="/industries"
                className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                Explore Your Industry
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </motion.div>

            {/* Right Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
              onMouseEnter={() => setIsCarouselPaused(true)}
              onMouseLeave={() => setIsCarouselPaused(false)}
            >
              {/* Industry Cards Carousel - With Drag Support */}
              <div className="relative overflow-hidden rounded-3xl">
                <motion.div
                  className="flex cursor-pointer hover:cursor-grab active:cursor-grabbing"
                  drag="x"
                  dragConstraints={{
                    left: -((industriesData.length - 1) * 340),
                    right: 0
                  }}
                  dragElastic={0.2}
                  dragMomentum={false}
                  onDragStart={() => {
                    // Disable auto-advance while dragging
                  }}
                  onDragEnd={(event, info) => {
                    const threshold = 50;
                    const velocity = info.velocity.x;
                    const offset = info.offset.x;

                    if (Math.abs(offset) > threshold || Math.abs(velocity) > 500) {
                      if (offset > 0 || velocity > 500) {
                        if (currentIndustryIndex > 0) {
                          setCurrentIndustryIndex(currentIndustryIndex - 1);
                        }
                      } else if (offset < 0 || velocity < -500) {
                        if (currentIndustryIndex < industriesData.length - 1) {
                          setCurrentIndustryIndex(currentIndustryIndex + 1);
                        }
                      }
                    }
                  }}
                  animate={{
                    x: -currentIndustryIndex * 340
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 40,
                    mass: 1
                  }}
                  whileDrag={{
                    scale: 0.98
                  }}
                >
                  {industriesData.map((industry, index) => {
                    const Icon = industry.icon;
                    return (
                      <motion.div
                        key={industry.name}
                        className="min-w-[320px] h-[450px] bg-white rounded-3xl overflow-hidden shadow-2xl relative group hover:scale-105 transition-all duration-500 border border-gray-100 mr-5"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {/* Image - More Visible */}
                        <img
                          src={industry.image}
                          alt={industry.name}
                          className="w-full h-full object-cover opacity-90 pointer-events-none"
                        />

                        {/* Blue Gradient Blur at Top - Lighter */}
                        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-blue-600/50 via-blue-700/30 to-transparent backdrop-blur-sm"></div>

                        {/* Industry-specific gradient overlay - Much Lighter */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient}`}></div>

                        {/* Bottom dark gradient for text readability */}
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/30 to-transparent"></div>

                        <div className="absolute bottom-0 left-0 right-0 p-8 text-white pointer-events-none">
                          <div className="flex items-center mb-3">
                            <Icon className="w-6 h-6 mr-3 text-white" />
                            <h3 className="text-2xl font-bold text-white">{industry.name}</h3>
                          </div>
                          <p className="text-white/90 mb-4 text-sm leading-relaxed">
                            {industry.description}
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {industry.badges.map((badge, badgeIndex) => (
                              <span
                                key={badgeIndex}
                                className="bg-white/25 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium border border-white/20"
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center mt-8 space-x-3">
                {industriesData.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToIndustry(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentIndustryIndex === index
                      ? 'bg-blue-600 shadow-sm scale-125'
                      : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                  />
                ))}
              </div>

              {/* Navigation Arrows - Better Positioning */}
              <button
                onClick={prevIndustry}
                className="absolute top-1/2 -translate-y-1/2 left-4 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 border border-gray-200 z-10"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
              <button
                onClick={nextIndustry}
                className="absolute top-1/2 -translate-y-1/2 right-4 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 border border-gray-200 z-10"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </button>
            </motion.div>
          </div>
        </div>
      </section >



      {/* 9. CTA SECTION - With Cursor Following Light Effect */}
      {/* 9. CTA SECTION - Redesigned Banner Style */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-[#001540] rounded-none overflow-hidden p-12 lg:p-16 shadow-2xl"
          >
            {/* Background Graphic/Curve Effect */}
            <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-white/5 skew-x-12 transform translate-x-20"></div>
            <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-blue-900/20 rounded-l-full blur-3xl transform translate-x-20"></div>

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              {/* Left Content */}
              <div className="w-full lg:w-2/3 space-y-6 text-left">
                <div className="text-blue-300 font-bold tracking-widest text-sm uppercase mb-2">
                  TAKE ACTION
                </div>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight">
                  Let's Build the Backbone <br className="hidden lg:block" />
                  of Your Digital Future
                </h2>
                <p className="text-lg text-blue-100 max-w-2xl font-light">
                  Ready to modernize your infrastructure? Our experts are here to design and implement
                  the perfect data center solution, with proactive protection and 24/7 expert support.
                </p>
              </div>

              {/* Right Buttons */}
              <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center bg-white text-[#001540] px-8 py-4 text-base font-bold transition-all duration-300 hover:bg-gray-100 min-w-[240px]"
                >
                  Get Your Free Assessment
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center border border-white text-white px-8 py-4 text-base font-bold transition-all duration-300 hover:bg-white/10 min-w-[200px]"
                >
                  View All Services
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default DataCenterPage;
